import { getAccessToken, setTokens, clearTokens } from '@/helpers'
import { ActionTree, ActionContext } from 'vuex'
import CONSTANTS from '@/constants'
import { Role, User } from '../../types'
import router from '../../../router'

import {
  loginUser,
  fetchStatus,
  logoutUser,
  postMFACode,
  fetchMFACode,
  postConfirmNewAccount,
  postForgotPassword,
  postNewPasswordByCode,
  postChangePassword,
  fetchSampleAvatars,
  postUserAvatar,
  postChangePhoneNumber,
  postChangeMFAMethod,
  fetchUserPreferences,
  putUserPreferences,
  fetchUserWithSAQuestEmbed,
} from '../../../models/auth'
import { AuthState } from './state'

const isUserAnAdmin = (roles: Array<Role>) => {
  const userRoles = roles.map(role => role.name)
  const isAdmin: boolean = userRoles.some(name =>
    CONSTANTS.ROLES.INE_ADMIN.includes(name)
  )
  return isAdmin
}

const isUserAContentCreator = (roles: Array<Role>) => {
  const userRoles = roles.map(role => role.name)
  return userRoles.includes(CONSTANTS.ROLES.CONTENT_CREATOR)
}

const isUserEnrolledInSecurityAwareness = (user: User) => {
  const questData = user[CONSTANTS.SECURITY_AWARENESS.QUEST_SLUG]?.data ?? {}
  const accepted = questData?.accepted_at ?? ''
  return !!accepted && accepted.length > 0
}

const setAuthState = async (commit: Function, user: User) => {
  const {
    id: uaaId,
    email,
    content_api_id,
    account_id,
    team_ids,
    origin,
    mfa_method,
    mfa_required,
    subscriber_status,
    roles: { data: roles },
    profile: { data: profile },
  } = user
  if (user?.tokens?.data) {
    const { Bearer, Refresh } = user.tokens.data
    await setTokens({ Bearer, Refresh })
  }
  commit('IS_ADMIN_UPDATED', isUserAnAdmin(roles))
  commit('IS_CONTENT_CREATOR_UPDATED', isUserAContentCreator(roles))
  commit('SUBSCRIPTION_STATUS_UPDATED', subscriber_status)
  commit('PROFILE_UPDATED', {
    uaaId,
    email,
    content_api_id,
    account_id,
    team_ids,
    origin,
    mfa_method,
    mfa_required,
    ...profile,
  })
  commit('AVATAR_UPDATED', profile.avatar)
  if (process.env.VUE_APP_ENVIRONMENT === 'production') {
    // @ts-expect-error
    window.dataLayer.push({ event: 'start_pw', pw_user_email: email })
  }
}

const fetchRequiredAuthDetails = async (
  context: ActionContext<AuthState, {}>
) => {
  if (!context.state.redirectUrl) {
    if (!context.getters.isFreeTrialUser) {
      await context.dispatch(
        'subscriptions/getSubscriptions',
        {},
        { root: true }
      )
    }
    if (context.getters.isFreeTrialUser) {
      await context.dispatch('subscriptions/getPassTypes', {}, { root: true })
    }
    if (context.getters.isBusinessUser) {
      await context.dispatch('getSAEnrollmentStatus')
    } else {
      context.commit('SECURITY_AWARENESS_ENROLLMENT_STATUS_UPDATED', false)
    }
    await context.dispatch('getProfilePreferences')
  }
}

const actions: ActionTree<AuthState, {}> = {
  login: async (
    context: ActionContext<AuthState, {}>,
    formData: { email: string; password: string; recaptcha: string }
  ) => {
    try {
      const user = await loginUser(formData)
      await setAuthState(context.commit, user)
      const { mfa_method } = user
      if (mfa_method && mfa_method.length) {
        context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', true)
      } else {
        await fetchRequiredAuthDetails(context)
        context.commit('IS_AUTHENTICATED_UPDATED', true)
      }
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  tryAutoLogin: async (context: ActionContext<AuthState, {}>) => {
    const token = getAccessToken()
    if (token) {
      try {
        const user = await fetchStatus()
        await setAuthState(context.commit, user)
        const heldToken = window.localStorage.getItem('heldToken')
        if (heldToken) {
          context.dispatch('admin/isImpersonating', true, { root: true })
        }
        await fetchRequiredAuthDetails(context)
        context.commit('IS_AUTHENTICATED_UPDATED', true)
      } catch (e) {
        await clearTokens()
        const query: { [k: string]: any } = {
          error: 'Your session has expired. Please login to continue.',
        }
        if (context.state.redirectUrl)
          query.redirect_url = context.state.redirectUrl
        router.push({
          name: 'login',
          query: {
            ...query,
          },
        })
        context.commit('PROFILE_UPDATED', '')
        context.commit('IS_AUTHENTICATED_UPDATED', false)
      }
    } else {
      await clearTokens()
      context.commit('PROFILE_UPDATED', '')
      context.commit('IS_AUTHENTICATED_UPDATED', false)
    }
  },

  federatedLogin: async (context: ActionContext<AuthState, {}>) => {
    const token = getAccessToken()
    if (token) {
      try {
        const user = await fetchStatus()
        await setAuthState(context.commit, user)
        await fetchRequiredAuthDetails(context)
        context.commit('IS_AUTHENTICATED_UPDATED', true)
      } catch (error) {
        const { code } = error
        if (code && code === 'MFAEXCEPTION-REQUIRED') {
          context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', true)
        } else {
          await clearTokens()
          context.commit('PROFILE_UPDATED', '')
          context.commit('IS_AUTHENTICATED_UPDATED', false)
          router.push({
            name: 'login',
            params: {
              error:
                'We were unable to log you in with that method. Please try again.',
            },
          })
        }
      }
    } else {
      await clearTokens()
      context.commit('PROFILE_UPDATED', '')
      context.commit('IS_AUTHENTICATED_UPDATED', false)
    }
  },

  logout: async ({
    commit,
    dispatch,
  }: {
    commit: Function
    dispatch: Function
  }) => {
    try {
      await logoutUser()
    } finally {
      await clearTokens()
      commit('PROFILE_UPDATED', '')
      dispatch('admin/isImpersonating', false, { root: true })
      commit('IS_ADMIN_UPDATED', false)
      commit('IS_AUTHENTICATED_UPDATED', false)
      commit('SUBSCRIPTION_STATUS_UPDATED', '')
      commit('subscriptions/CURRENT_SUBSCRIPTIONS_UPDATED', [], { root: true })
    }
  },

  submitMFACode: async (
    context: ActionContext<AuthState, {}>,
    code: string
  ) => {
    try {
      const user = await postMFACode(code)
      context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', false)
      await setAuthState(context.commit, user)
      await fetchRequiredAuthDetails(context)
      context.commit('IS_AUTHENTICATED_UPDATED', true)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  resendMFACode: async (context: ActionContext<AuthState, {}>) => {
    try {
      await fetchMFACode()
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getSampleAvatars: async (context: ActionContext<AuthState, {}>) => {
    try {
      const response = await fetchSampleAvatars()
      context.commit('SAMPLE_AVATARS_UPDATED', response)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateUserAvatar: async (
    context: ActionContext<AuthState, {}>,
    fileAndType: { type: string; file: BinaryType | string }
  ) => {
    try {
      const response = await postUserAvatar(fileAndType)
      context.commit('AVATAR_UPDATED', response.data.avatar)
      context.dispatch('app/setNotification', 'Avatar updated', {
        root: true,
      })
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  confirmNewAccount: async (
    context: ActionContext<AuthState, {}>,
    codeAndUUID: {
      code: string
      username: string
    }
  ) => {
    try {
      await postConfirmNewAccount(codeAndUUID)
      router.push({ name: 'login' })
      context.dispatch(
        'app/setNotification',
        'Your account is ready! Please login to continue.',
        { root: true }
      )
    } catch (error) {
      router.push({ name: 'login' })
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  triggerForgotPassword: async (
    context: ActionContext<AuthState, {}>,
    email: string
  ) => {
    try {
      await postForgotPassword(email)
      context.dispatch(
        'app/setNotification',
        'An email has been sent with instructions on how to reset your password',
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateNewPasswordByCode: async (
    context: ActionContext<AuthState, {}>,
    formData: {
      code: string
      username: string
      password: string
    }
  ) => {
    try {
      await postNewPasswordByCode(formData)
      context.dispatch(
        'app/setNotification',
        'Your password has been successfully updated! Please login using the new password.',
        { root: true }
      )
      router.push({ name: 'login' })
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateUserPassword: async (
    context: ActionContext<AuthState, {}>,
    currentAndNewPasswords: {
      currentPassword: string
      password: string
    }
  ) => {
    try {
      await postChangePassword(currentAndNewPasswords)
      context.dispatch(
        'app/setNotification',
        'Your password has been successfully changed!',
        { root: true }
      )
      router.push({ name: 'home' })
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updatePhoneNumber: async (
    context: ActionContext<AuthState, {}>,
    phoneNumber: string
  ) => {
    try {
      const { preferred_phone_number } = await postChangePhoneNumber(
        phoneNumber
      )
      context.commit('PHONE_NUMBER_UPDATED', preferred_phone_number)
      context.dispatch(
        'app/setNotification',
        'Your phone number has been successfully updated!',
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateMFAMethod: async (
    context: ActionContext<AuthState, {}>,
    method: string | null = null
  ) => {
    try {
      await postChangeMFAMethod(method)
      context.commit('MFA_METHOD_UPDATED', method)
      context.dispatch(
        'app/setNotification',
        method && method.length
          ? 'You now have two factor authentication enabled!'
          : 'Two factor authentication has been disabled',
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getProfilePreferences: async (context: ActionContext<AuthState, {}>) => {
    try {
      const preferences = await fetchUserPreferences()
      context.commit('PROFILE_PREFERENCES_UPDATED', preferences)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  submitProfilePreferences: async (
    context: ActionContext<AuthState, {}>,
    preferences: object
  ) => {
    try {
      await putUserPreferences(preferences)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  setIsTwoFactorRequired: (
    context: ActionContext<AuthState, {}>,
    isTwoFactorRequired: boolean
  ) => {
    context.commit('IS_TWO_FACTOR_REQUIRED_UPDATED', isTwoFactorRequired)
  },

  setIsAuthLoading: ({ commit }, isLoading: boolean) => {
    commit('IS_AUTH_LOADING_UPDATED', isLoading)
  },

  setRedirectUrl: (
    context: ActionContext<AuthState, {}>,
    redirectUrl: string
  ) => {
    context.commit('REDIRECT_URL_UPDATED', redirectUrl)
  },

  saveRedirectUrl: (context: ActionContext<AuthState, {}>) => {
    if (context.state.redirectUrl) {
      window.localStorage.setItem('redirectUrl', context.state.redirectUrl)
    }
  },

  restoreRedirectUrl: (context: ActionContext<AuthState, {}>) => {
    const redirectUrl = window.localStorage.getItem('redirectUrl')
    if (redirectUrl) {
      context.commit('REDIRECT_URL_UPDATED', redirectUrl)
      window.localStorage.removeItem('redirectUrl')
    }
  },

  getSAEnrollmentStatus: async (context: ActionContext<AuthState, {}>) => {
    try {
      const userData = await fetchUserWithSAQuestEmbed()
      context.commit(
        'SECURITY_AWARENESS_ENROLLMENT_STATUS_UPDATED',
        isUserEnrolledInSecurityAwareness(userData)
      )
    } catch (error) {
      return error
    }
  },
}

export default actions
